import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchTicketsRequest,
  fetchTicketsSuccess,
  fetchTicketsFailure,
  searchTicketsRequest,
  searchTicketsSuccess,
  searchTicketsFailure,
  fetchTicketRequest,
  fetchTicketSuccess,
  fetchTicketFailure,
  createTicketRequest,
  createTicketSuccess,
  createTicketFailure,
  updateTicketRequest,
  updateTicketSuccess,
  updateTicketFailure,
  fetchTicketMessagesRequest,
  fetchTicketMessagesSuccess,
  fetchTicketMessagesFailure,
  createTicketMessageRequest,
  createTicketMessageSuccess,
  createTicketMessageFailure,
  fetchTicketTagsRequest,
  fetchTicketTagsSuccess,
  fetchTicketTagsFailure,
  fetchTicketStatsRequest,
  fetchTicketStatsSuccess,
  fetchTicketStatsFailure,
} from '../slices/ticket.slice';
import {
  getTickets,
  searchTickets,
  getTicketById,
  createTicket,
  updateTicket,
  getTicketComments,
  createTicketComment,
  getTicketTags,
  getTicketStats,
} from '@/services/ticketService';
import { 
  Ticket, 
  TicketMessage, 
  TicketTag,
  CreateTicketDto,
  UpdateTicketDto,
  CreateTicketMessageDto,
  FetchTicketsOptions,
  SearchTicketsOptions 
} from '@/types/models/ticket';
import { ApiError, PaginatedApiResponse, ApiResponse } from "@/lib/apiClient";

// Fetch Tickets Saga
function* fetchTicketsSaga(action: PayloadAction<FetchTicketsOptions>) {
  try {
    const response: PaginatedApiResponse<Ticket[]> = yield call(getTickets, action.payload);
    
    yield put(fetchTicketsSuccess({
      list: response.data,
      page: response.pagination.page,
      total: response.pagination.total,
      pageSize: response.pagination.pageSize,
      totalPages: response.pagination.totalPages,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchTicketsFailure(error));
    } else {
      yield put(
        fetchTicketsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Search Tickets Saga
function* searchTicketsSaga(action: PayloadAction<SearchTicketsOptions>) {
  try {
    const response: PaginatedApiResponse<Ticket[]> = yield call(searchTickets, action.payload);
    
    yield put(searchTicketsSuccess({
      list: response.data,
      page: response.pagination.page,
      total: response.pagination.total,
      pageSize: response.pagination.pageSize,
      totalPages: response.pagination.totalPages,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(searchTicketsFailure(error));
    } else {
      yield put(
        searchTicketsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Single Ticket Saga
function* fetchTicketSaga(action: PayloadAction<{ ticketId: string }>) {
  try {
    const { ticketId } = action.payload;
    const response: ApiResponse<Ticket> = yield call(getTicketById, ticketId);
    
    yield put(fetchTicketSuccess({ ticket: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchTicketFailure(error));
    } else {
      yield put(
        fetchTicketFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Create Ticket Saga
function* createTicketSaga(action: PayloadAction<CreateTicketDto>) {
  try {
    const response: ApiResponse<Ticket> = yield call(createTicket, action.payload);
    
    yield put(createTicketSuccess({ ticket: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(createTicketFailure(error));
    } else {
      yield put(
        createTicketFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Update Ticket Saga
function* updateTicketSaga(action: PayloadAction<{ ticketId: string; ticket: UpdateTicketDto }>) {
  try {
    const { ticketId, ticket } = action.payload;
    const response: ApiResponse<Ticket> = yield call(updateTicket, ticketId, ticket);
    
    yield put(updateTicketSuccess({ ticket: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(updateTicketFailure(error));
    } else {
      yield put(
        updateTicketFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Ticket Messages Saga
function* fetchTicketMessagesSaga(action: PayloadAction<{ ticketId: string; page?: number; limit?: number }>) {
  try {
    const { ticketId, page, limit } = action.payload;
    const response: PaginatedApiResponse<TicketMessage[]> = yield call(getTicketComments, ticketId, page, limit);
    
    yield put(fetchTicketMessagesSuccess({ messages: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchTicketMessagesFailure(error));
    } else {
      yield put(
        fetchTicketMessagesFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Create Ticket Message Saga
function* createTicketMessageSaga(action: PayloadAction<CreateTicketMessageDto>) {
  try {
    const response: ApiResponse<TicketMessage> = yield call(createTicketComment, action.payload);
    
    yield put(createTicketMessageSuccess({ message: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(createTicketMessageFailure(error));
    } else {
      yield put(
        createTicketMessageFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Ticket Tags Saga
function* fetchTicketTagsSaga() {
  try {
    const response: ApiResponse<TicketTag[]> = yield call(getTicketTags);
    
    yield put(fetchTicketTagsSuccess({ tags: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchTicketTagsFailure(error));
    } else {
      yield put(
        fetchTicketTagsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Ticket Stats Saga
function* fetchTicketStatsSaga(action: PayloadAction<{ period?: string }>) {
  try {
    const { period } = action.payload;
    const response: ApiResponse<any> = yield call(getTicketStats, period as any);
    
    yield put(fetchTicketStatsSuccess({
      totalTickets: response.data.totalTickets,
      openTickets: response.data.openTickets,
      resolvedTickets: response.data.resolvedTickets,
      avgResponseTime: response.data.avgResponseTime,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchTicketStatsFailure(error));
    } else {
      yield put(
        fetchTicketStatsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

export default function* ticketsRootSaga() {
  yield takeLatest(fetchTicketsRequest.type, fetchTicketsSaga);
  yield takeLatest(searchTicketsRequest.type, searchTicketsSaga);
  yield takeLatest(fetchTicketRequest.type, fetchTicketSaga);
  yield takeEvery(createTicketRequest.type, createTicketSaga);
  yield takeEvery(updateTicketRequest.type, updateTicketSaga);
  yield takeLatest(fetchTicketMessagesRequest.type, fetchTicketMessagesSaga);
  yield takeEvery(createTicketMessageRequest.type, createTicketMessageSaga);
  yield takeLatest(fetchTicketTagsRequest.type, fetchTicketTagsSaga);
  yield takeLatest(fetchTicketStatsRequest.type, fetchTicketStatsSaga);
}
